import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Settings as SettingsIcon, 
  Users, 
  Database, 
  ShieldCheck, 
  Bell, 
  History, 
  Building2,
  Info,
  ChevronRight
} from 'lucide-react';

const Settings = () => {
  const [userRole, setUserRole] = useState('user');
  
  // Lấy role từ local storage khi component mount
  useEffect(() => {
    try {
      const userInfoStr = localStorage.getItem('user_info');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        if (userInfo && userInfo.role) {
          setUserRole(userInfo.role.toLowerCase());
        }
      }
    } catch (e) {
      console.error("Lỗi khi đọc thông tin user từ localStorage:", e);
    }
  }, []);

  // Danh sách toàn bộ menu (bao gồm cả các menu yêu cầu quyền admin)
  const allMenuItems = [
    {
      id: 'categories',
      title: 'Danh mục',
      description: 'ICD10, Thuốc, DVKT',
      path: '/settings/categories',
      icon: <Database className="w-7 h-7 text-blue-600" />,
      color: 'bg-blue-100',
      hoverBorder: 'hover:border-blue-300',
      requiresAdmin: false
    },
    {
      id: 'system',
      title: 'Cấu hình',
      description: 'Tham số hệ thống & XML',
      path: '/settings/system',
      icon: <SettingsIcon className="w-7 h-7 text-purple-600" />,
      color: 'bg-purple-100',
      hoverBorder: 'hover:border-purple-300',
      requiresAdmin: false
    },
    {
      id: 'users',
      title: 'Người dùng',
      description: 'Quản lý & Phân quyền',
      path: '/settings/users',
      icon: <Users className="w-7 h-7 text-green-600" />,
      color: 'bg-green-100',
      hoverBorder: 'hover:border-green-300',
      requiresAdmin: true // Chỉ admin mới thấy
    },
    {
      id: 'security',
      title: 'Bảo mật',
      description: 'Mật khẩu & Nhật ký',
      path: '/settings/security',
      icon: <ShieldCheck className="w-7 h-7 text-red-600" />,
      color: 'bg-red-100',
      hoverBorder: 'hover:border-red-300',
      requiresAdmin: false
    },
    {
      id: 'logs',
      title: 'Nhật ký',
      description: 'Lịch sử xử lý dữ liệu',
      path: '/settings/logs',
      icon: <History className="w-7 h-7 text-orange-600" />,
      color: 'bg-orange-100',
      hoverBorder: 'hover:border-orange-300',
      requiresAdmin: false
    },
    {
      id: 'notifications',
      title: 'Thông báo',
      description: 'Cấu hình cảnh báo lỗi',
      path: '/settings/notifications',
      icon: <Bell className="w-7 h-7 text-yellow-600" />,
      color: 'bg-yellow-100',
      hoverBorder: 'hover:border-yellow-300',
      requiresAdmin: false
    },
    {
      id: 'facilities', 
      title: 'Cơ sở KCB', 
      description: 'Quản lý danh sách cơ sở', 
      path: '/settings/facilities', 
      icon: <Building2 className="w-7 h-7 text-cyan-600" />,
      color: 'bg-cyan-100',
      hoverBorder: 'hover:border-cyan-300',
      requiresAdmin: true // Chỉ admin mới thấy
    },
    {
      id: 'about',
      title: 'Thông tin',
      description: 'Phiên bản hệ thống 1.0',
      path: '/settings/about',
      icon: <Info className="w-7 h-7 text-gray-600" />,
      color: 'bg-gray-200',
      hoverBorder: 'hover:border-gray-400',
      requiresAdmin: false
    },
  ];

  // Lọc ra các menu mà user hiện tại có quyền xem
  const visibleMenuItems = allMenuItems.filter(item => {
    if (item.requiresAdmin && userRole !== 'admin') {
      return false; // Nếu menu yêu cầu quyền admin nhưng user không phải admin -> Ẩn
    }
    return true; // Các trường hợp khác -> Hiện
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Grid Menu */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-8">
          {visibleMenuItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`group relative flex flex-col items-center p-6 bg-white rounded-[2.5rem] shadow-sm border border-transparent transition-all duration-300 ${item.hoverBorder} hover:shadow-xl hover:-translate-y-1 active:scale-95`}
            >
              <div className={`w-16 h-16 ${item.color} rounded-3xl flex items-center justify-center mb-4 transition-transform duration-500 group-hover:rotate-[10deg]`}>
                {item.icon}
              </div>
              <h3 className="text-sm font-bold text-slate-700 text-center group-hover:text-blue-600 transition-colors">
                {item.title}
              </h3>
              <p className="hidden sm:block text-[10px] text-slate-400 mt-2 text-center leading-tight px-2">
                {item.description}
              </p>
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight size={14} className="text-slate-300" />
              </div>
            </Link>
          ))}
        </div>

        {/* Footer Info */}
        <div className="mt-12 bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-ping absolute inset-0"></div>
              <div className="w-3 h-3 bg-green-500 rounded-full relative"></div>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-700">Máy chủ: Hoạt động ổn định</p>
              <p className="text-xs text-slate-400">Phiên bản Backend: Python 3.13 (FastAPI)</p>
            </div>
          </div>
          <div className="h-px w-full md:w-px md:h-8 bg-slate-100"></div>
          <div className="text-center md:text-right">
            <p className="text-xs font-medium text-slate-500">Cơ sở: <span className="text-blue-600">Sóc Trăng (94019)</span></p>
            <p className="text-[10px] text-slate-400 mt-1">Cập nhật cuối: 27/03/2026 15:54</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;